/**
 * Native fetch HTTP client (axios replacement).
 *
 * Exposes the small slice of the axios API the app actually used —
 * get/post/put/patch/delete returning `{ data, status, statusText, headers }`
 * and throwing an axios-shaped error (`error.response.{data,status,headers}`,
 * or `error.response === undefined` for network failures) — so call sites and
 * error helpers keep working unchanged.
 *
 * It also carries the behaviour that previously lived in the axios interceptors:
 *  - Request: inject `Authorization: Bearer <token>` and `X-Tenant-Token`.
 *  - Response: on a 401 from a non-auth endpoint, call the auth store's shared
 *    refreshSession() and replay the original request once. A refresh failure is
 *    the definitive "session expired" signal and triggers logout. Auth endpoints
 *    (/api/v1/auth/*) just reject so the caller can handle it.
 */
import { tokenService } from "@/services/tokenService";
// Imported lazily-at-call-time (not at module load) to avoid the auth-store <-> client
// circular import being evaluated before Pinia is ready.
import { useAuthUserStore } from "@/stores/auth";

const baseURL = import.meta.env.VITE_API_URL;

class HttpError extends Error {
  constructor(message, { response, config, cause } = {}) {
    super(message);
    this.name = "HttpError";
    // `response` is undefined for network errors (mirrors axios), otherwise
    // { data, status, statusText, headers }.
    this.response = response;
    this.config = config;
    if (cause) this.cause = cause;
  }
}

// Join baseURL + url the way axios did: absolute URLs pass through untouched,
// relative ones get exactly one slash between base and path (so both
// "/api/..." and "api/..." resolve correctly).
function buildURL(url) {
  if (/^https?:\/\//i.test(url)) return url;
  const base = (baseURL ?? "").replace(/\/$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;
  return base + path;
}

// Serialize the request body and report the content-type fetch won't set for us.
// Plain objects/arrays -> JSON; FormData / URLSearchParams / Blob / string pass
// through so the browser can set the correct content-type (incl. the multipart
// boundary for FormData).
function prepareBody(data) {
  if (data == null) return { body: undefined, contentType: undefined };
  if (
    data instanceof FormData ||
    data instanceof URLSearchParams ||
    data instanceof Blob ||
    data instanceof ArrayBuffer ||
    typeof data === "string"
  ) {
    return { body: data, contentType: undefined };
  }
  if (typeof data === "object") {
    return { body: JSON.stringify(data), contentType: "application/json" };
  }
  return { body: data, contentType: undefined };
}

function headersToObject(headers) {
  const obj = {};
  headers.forEach((value, key) => {
    obj[key] = value;
  });
  return obj;
}

// Parse the response body the way axios did by default. Axios' default
// transformResponse attempts JSON.parse on ANY string body regardless of the
// Content-Type header and silently falls back to the raw text. We must match
// that: some endpoints (e.g. /api/v1/files/{id}/load) return JSON without an
// `application/json` content-type, and gating on the header would hand callers
// a raw string instead of the parsed object. An empty body stays "" (matches
// axios, and is safe for accidental `.prop` access at call sites).
async function parseBody(response) {
  const text = await response.text();
  if (text === "") return text;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// Single fetch round-trip: build headers, inject auth, send, and normalize the
// result/error into the axios shape. No retry logic here — that lives in
// requestWithRefresh so the replay can reuse this unchanged.
async function performFetch(config) {
  const { method = "get", url, data, headers: customHeaders } = config;

  const headers = new Headers();
  if (customHeaders) {
    for (const [key, value] of Object.entries(customHeaders)) {
      if (value != null) headers.set(key, value);
    }
  }

  const { body, contentType } = prepareBody(data);
  if (data instanceof FormData) {
    // Let the browser set multipart/form-data with the correct boundary;
    // a manually-supplied content-type here would omit it and break the upload.
    headers.delete("Content-Type");
  } else if (contentType && !headers.has("Content-Type")) {
    headers.set("Content-Type", contentType);
  }

  // Request interceptor equivalent: attach the current access + tenant tokens.
  const token = tokenService.getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const tenantToken = tokenService.getTenantToken();
  if (tenantToken) headers.set("X-Tenant-Token", tenantToken);

  let response;
  try {
    response = await fetch(buildURL(url), {
      method: method.toUpperCase(),
      headers,
      body,
      credentials: "include", // send/receive the CSRF + refresh-token cookies
    });
  } catch (networkError) {
    // No HTTP response (DNS, offline, CORS) -> axios-style network error.
    throw new HttpError(networkError.message || "Network Error", {
      config,
      cause: networkError,
    });
  }

  const result = {
    data: await parseBody(response),
    status: response.status,
    statusText: response.statusText,
    headers: headersToObject(response.headers),
    config,
  };

  if (response.ok) return result;

  throw new HttpError(`Request failed with status code ${response.status}`, {
    response: result,
    config,
  });
}

// Response interceptor equivalent: on a 401 for a protected endpoint, refresh
// the session via the store's shared in-flight promise and replay once.
async function requestWithRefresh(config) {
  try {
    return await performFetch(config);
  } catch (error) {
    const isAuthEndpoint = config.url?.startsWith("/api/v1/auth/");

    if (
      error.response?.status === 401 &&
      !isAuthEndpoint &&
      !config._retry
    ) {
      const store = useAuthUserStore();
      if (store.getAuthenticatedUser && !store.getGuest) {
        config._retry = true;
        try {
          await store.refreshSession();
          return await performFetch(config); // picks up the fresh token
        } catch {
          store.logout();
          throw error;
        }
      }
    }

    throw error;
  }
}

// Build a method shortcut. Bodyless verbs use (url, config); the rest use
// (url, data, config) — matching the axios signatures the app relied on.
function methodWithoutBody(method) {
  return (url, config = {}) => requestWithRefresh({ ...config, method, url });
}

function methodWithBody(method) {
  return (url, data, config = {}) =>
    requestWithRefresh({ ...(config ?? {}), method, url, data });
}

export const httpClient = {
  request: (config) => requestWithRefresh(config),
  get: methodWithoutBody("get"),
  delete: methodWithoutBody("delete"),
  head: methodWithoutBody("head"),
  post: methodWithBody("post"),
  put: methodWithBody("put"),
  patch: methodWithBody("patch"),
};

export default httpClient;
